/**
 * Subscription State Manager
 * Handles real-time subscription state updates across the app
 */

type SubscriptionStateListener = () => void;

class SubscriptionStateManager {
  private listeners: Set<SubscriptionStateListener> = new Set();

  /**
   * Subscribe to subscription state changes
   */
  subscribe(listener: SubscriptionStateListener): () => void {
    this.listeners.add(listener);

    // Return unsubscribe function
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Notify all listeners that subscription state has changed
   * Call this after successful subscription upgrades/cancellations
   */
  notifyStateChange(): void {
    console.log('🔄 [SubscriptionStateManager] Notifying state change to', this.listeners.size, 'listeners');

    this.listeners.forEach(listener => {
      try {
        listener();
      } catch (error) {
        console.error('Error in subscription state listener:', error);
      }
    });
  }

  /**
   * Clear all subscription caches and refresh state
   * This ensures fresh data is fetched after subscription changes
   */
  async refreshAllSubscriptionData(): Promise<void> {
    console.log('🔄 [SubscriptionStateManager] Refreshing all subscription data');

    // Clear all caches
    const { clearFeatureAccessCache } = await import('../components/PremiumFeatureGate');
    clearFeatureAccessCache(); // Clear all cached feature access

    // Notify all components to refresh
    this.notifyStateChange();
  }

  /**
   * Get listener count for debugging
   */
  getListenerCount(): number {
    return this.listeners.size;
  }
}

// Export singleton instance
export const subscriptionStateManager = new SubscriptionStateManager();

// Export convenience hook for React components
export const useSubscriptionStateRefresh = () => {
  return {
    refreshSubscriptionState: () => subscriptionStateManager.refreshAllSubscriptionData(),
    notifyStateChange: () => subscriptionStateManager.notifyStateChange(),
  };
};

// Development helper function - can be called from console during debugging
if (__DEV__ && typeof global !== 'undefined') {
  (global as typeof global & { refreshSubscriptionState?: () => void }).refreshSubscriptionState = () => {
    console.log('🔄 [DEV] Manual subscription state refresh triggered');
    subscriptionStateManager.refreshAllSubscriptionData();
  };
}
